import { FC, useCallback, useEffect } from 'react';
import { CardContent } from '@mui/material';
import { Hooks } from '@wellstone-solutions/common';
import { TextField, Typography } from '@wellstone-solutions/web';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { PasswordField } from '@/components/ui/Form';
import { Alert } from '@components/Alert';
import { CardHeader } from '@components/ui/Card';
import { Link } from '@components/ui/Link';
import { Form } from '@modules/auth/components/Form';
import { LOG_IN_FORM_ID } from '@modules/auth/constants';
import { useAuthenticationFlow } from '@modules/auth/hooks/useAuthenticationFlow';
import { useLogin } from '@modules/auth/queries/use-login';
import { LogInSchema } from '@modules/auth/schemas/log-in-schema';
import { useDiscourseAuthTokens } from '@modules/auth/store';
import { translateFromMap } from '@utils/translate-from-map';

import type { AuthSuccessData } from '@modules/auth/hooks/useAuthenticationFlow';
import type { LogInSchemaType } from '@modules/auth/schemas/log-in-schema';
import type { TranslationMapType } from '@utils/translate-from-map';
import type { AxiosError, AxiosResponse } from 'axios';

const ALERTS: TranslationMapType = {
  default: 'auth.login.alerts.default',
  invalid_password: 'auth.login.alerts.invalid_password', // NOSONAR
  unverified_email: 'auth.login.alerts.unverified_email',
};

type LogInFormType = ReturnType<typeof Hooks.useForm<LogInSchemaType>>;

const LogIn: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const mutation = useLogin();
  const { setDiscourseAuthTokens } = useDiscourseAuthTokens();
  const { processLoginSuccess } = useAuthenticationFlow();

  const resolveAlertMessage = (
    error: AxiosError<{ code?: string }>
  ): string => {
    const code = error.response?.data.code;
    return translateFromMap(t, ALERTS, code);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sso = searchParams.get('sso');
    const sig = searchParams.get('sig');

    if (sso && sig) setDiscourseAuthTokens(sso, sig);
  }, [location.search, setDiscourseAuthTokens]);

  const handleSuccess = useCallback(
    async (data: AxiosResponse<AuthSuccessData>) => {
      await processLoginSuccess(data);
    },
    [processLoginSuccess]
  );

  return (
    <>
      <CardHeader
        title={t('auth.login.title')}
        subheaders={[
          <Trans
            key="auth.login.personalization_subheader"
            i18nKey="auth.login.personalization_subheader">
            After you log in, you get to personalize your account.
          </Trans>,
          <Trans key="auth.login.subheader" i18nKey="auth.login.subheader">
            Don't have an account?{' '}
            <Link to="/auth/sign-up" key="sign-up-link">
              Sign up now!
            </Link>
          </Trans>,
        ]}
      />
      <CardContent sx={styles.cardContent}>
        {mutation.isError && (
          <Alert severity="error">{resolveAlertMessage(mutation.error)}</Alert>
        )}
        <Form
          formId={LOG_IN_FORM_ID}
          schema={LogInSchema}
          initialValues={{ email: '', password: '' }}
          mutation={mutation}
          mutateOptions={{
            onSuccess: async (data) => {
              // hack the types since the Login response shape isn't correct in Orval
              const authData =
                data as unknown as AxiosResponse<AuthSuccessData>;
              await handleSuccess(authData);
            },
            // empty func is needed to trigger mutation.isError
            onError: () => {},
          }}>
          {(form) => {
            const formik = form as LogInFormType;

            return (
              <>
                <TextField
                  fullWidth
                  formField="email"
                  label={t('auth.email')}
                  type="email"
                  id="email"
                  inputProps={{ maxLength: 128 }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    formik.setFieldValue('email', e.target.value, false)
                  }
                  onBlur={() => formik.validateField('email')}
                  value={formik.values.email}
                  error={
                    formik.errors.email ||
                    (formik.submitCount > 0 && mutation.isError)
                  }
                />
                <PasswordField
                  fullWidth
                  formField="password"
                  label={t('auth.password')}
                  type="password"
                  id="password"
                  inputProps={{ maxLength: 128 }}
                  onChange={formik.handleFieldChange('password')}
                  value={formik.values.password}
                  error={formik.submitCount > 0 && mutation.isError}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={styles.formHelperText}>
                  <Trans i18nKey="auth.login.forgot_password">
                    Forgot your password?{' '}
                    <Link to="/auth/forgot-password">Reset it</Link>.
                  </Trans>
                </Typography>
              </>
            );
          }}
        </Form>
      </CardContent>
    </>
  );
};

const styles = {
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  formHelperText: {
    alignSelf: 'flex-start',
  },
};

export { LogIn };
